import { Box, HBox, VBox } from "@ui/box/Box"
import { DS, FormIntro, Glyph, UnderlineTabs } from "../ds/Kit"
import Button from "@ui/button/Button"
import { IconBank } from "@ui/icons/IconBank"
import { IconReport } from "@ui/icons/IconReport"
import { IconUserCheck } from "@ui/icons/IconUserCheck"
import { Page } from "@ui/page/Page"
import { Typo } from "@ui/text/Typo"
import { useWindowSize } from "@ui/utils/useResize"
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { GD } from "src/GD"
import { FormParser } from "src/view/FormParser"
import { InvitesList } from "../components/InvitesList"
import { UsersList } from "../components/UsersList"
import { S_UNIVERSAL_DIALOG } from "../components/universalDialog/signals"

const USER_TABS = ["All users", "Invites"] as const
const USERS_PANEL_BORDER = "#E8EDF3"
const USERS_PANEL_SHADOW = "0 1px 2px rgba(15, 23, 42, 0.03), 0 8px 24px rgba(15, 23, 42, 0.04)"

interface UserStatCardProps {
	label: string
	value: number | null
	detail: string
	icon: ReactNode
	accent: string
	soft: string
	selected?: boolean
	onClick?: () => void
}

const UserStatCard = ({
	label,
	value,
	detail,
	icon,
	accent,
	soft,
	selected,
	onClick
}: UserStatCardProps) => (
	<Box
		element={onClick ? "button" : "div"}
		type={onClick ? "button" : undefined}
		onClick={onClick}
		aria-label={onClick ? `Filter by ${label}` : undefined}
		aria-pressed={onClick ? Boolean(selected) : undefined}
		sx={{
			width: "100%",
			minWidth: 0,
			padding: "16px 18px",
			border: 0,
			borderRadius: 0,
			backgroundColor: selected ? soft : "transparent",
			boxShadow: selected ? `inset 0 -3px 0 ${accent}` : "none",
			fontFamily: "inherit",
			textAlign: "left",
			cursor: onClick ? "pointer" : "default",
			transition: "background-color .15s ease, box-shadow .15s ease",
			"&:hover": onClick ? { backgroundColor: selected ? soft : DS.soft } : {}
		}}
	>
		<HBox sx={{ alignItems: "center", gap: "12px", minWidth: 0 }}>
			<Box
				sx={{
					width: "36px",
					height: "36px",
					borderRadius: "11px",
					backgroundColor: soft,
					color: accent,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
					"& svg path": { fill: accent, fillOpacity: 1 }
				}}
			>
				{icon}
			</Box>
			<VBox gap='2px' sx={{ minWidth: 0, flex: 1 }}>
				<Typo sx={{ color: DS.muted, fontSize: "12px", fontWeight: 500, whiteSpace: "nowrap" }}>{label}</Typo>
				<Typo sx={{ color: DS.ink, fontSize: "22px", lineHeight: 1.1, fontWeight: 700 }}>{value ?? "—"}</Typo>
				<Typo sx={{ color: DS.faint, fontSize: "12px", lineHeight: 1.3 }}>{detail}</Typo>
			</VBox>
		</HBox>
	</Box>
)

export const UsersPage = () => {
	const { width: viewportWidth } = useWindowSize()
	const isMobile = viewportWidth <= 768
	const initialRoleFilter =
		new URLSearchParams(window.location.search).get("role") === "buyer" ? "buyer" : "all"
	const [roleFilter, setRoleFilter] = useState(initialRoleFilter)
	const [activeTab, setActiveTab] = useState<(typeof USER_TABS)[number]>("All users")
	const [usersData] = GD.REQ_USERS.useRequest()
	const statsAvailable = !usersData?.error && Array.isArray(usersData?.data)
	const statsStatus = usersData?.error ? "Unavailable" : "Loading…"

	useEffect(() => () => S_UNIVERSAL_DIALOG.invoke(null), [])

	const stats = useMemo(() => {
		const data = (usersData?.data || []) as IUserSaved[]
		const now = new Date()
		const getCounts = (role: string) => {
			const roleUsers = data.filter((user) => user.company_role === role)
			const active = roleUsers.filter((user) => user.company_status === "active").length
			return { total: roleUsers.length, active }
		}

		return {
			buyers: getCounts("buyer"),
			teamLeads: getCounts("teamlead"),
			admins: getCounts("admin"),
			suspended: data.filter((user) => user.company_status !== "active").length,
			newUsers: data.filter((user) => {
				const createdAt = new Date(user.created_at)
				return createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear()
			}).length
		}
	}, [usersData])

	const toggleRoleFilter = (role: string) => {
		setRoleFilter((current) => current === role ? "all" : role)
	}

	const handleAddUser = useCallback((prefill?: Pick<IInvite, "name" | "email" | "role">) => {
		S_UNIVERSAL_DIALOG.invoke({
			label: "Invite User",
			content: (
				<>
					<FormIntro icon={Glyph.invite} title="Invite a user" description="We'll email an invitation link. They join the team and role you choose." />
					<FormParser
						key='inviteUser'
						onSubmit={async (data) => {
							const response = await GD.REQ_INVITE_USER.request({
								email: data.email,
								role: data.role,
								name: data.name,
								team_uid: data.team_uid
							})
							if (response.error) return response
							S_UNIVERSAL_DIALOG.invoke(null)
							toast.success("User invited successfully")
							return {}
						}}
						onRequest={async () => {
							const template = await GD.REQ_INVITE_TEMPLATE.request()
							if (!template.data) throw new Error(template.error?.message || "Could not load invitation options.")
							return {
								...template.data,
								...(prefill && { name: prefill.name, email: prefill.email, role: prefill.role }),
								$__team_uid_label: "Team",
								$__team_uid_details: "Choose the team this user will join.",
								$__role_details: "Access role for this workspace."
							}
						}}
						primaryButton={{ title: "Send Invite" }}
						secondaryButton={{ title: "Cancel", onClick: () => S_UNIVERSAL_DIALOG.invoke(null) }}
					/>
				</>
			),
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
		})
	}, [])

	return (
		<Page
			label='Users'
			description='Manage users, teams and buyer balances.'
			sectionRightContent={
				<Button
					onClick={() => handleAddUser()}
					title='Invite user'
					variant='primarySmall'
					sx={{ padding: "9px 16px", borderRadius: "9px", boxShadow: "0 4px 12px rgba(39, 68, 93, 0.16)" }}
				/>
			}
			sx={{ boxShadow: "none", paddingTop: "24px" }}
		>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
					flexShrink: 0,
					overflow: "hidden",
					borderRadius: DS.radius,
					border: `1px solid ${DS.line}`,
					background: DS.surface,
					boxShadow: DS.shadow,
					"& > *:not(:first-of-type)": { borderLeft: `1px solid ${DS.line}` },
					"@media (max-width: 1100px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }
				}}
			>
				<UserStatCard
					label='Buyers'
					value={statsAvailable ? stats.buyers.total : null}
					detail={statsAvailable ? `${stats.buyers.active} active` : statsStatus}
					icon={<IconUserCheck size={19} />}
					accent='#0F8F72'
					soft='#ECFDF7'
					selected={roleFilter === "buyer"}
					onClick={() => toggleRoleFilter("buyer")}
				/>
				<UserStatCard
					label='Team leads'
					value={statsAvailable ? stats.teamLeads.total : null}
					detail={statsAvailable ? `${stats.teamLeads.active} active` : statsStatus}
					icon={<IconBank size='19px' />}
					accent='#2563EB'
					soft='#EFF6FF'
					selected={roleFilter === "teamlead"}
					onClick={() => toggleRoleFilter("teamlead")}
				/>
				<UserStatCard
					label='Admins'
					value={statsAvailable ? stats.admins.total : null}
					detail={statsAvailable ? `${stats.admins.active} active` : statsStatus}
					icon={<IconReport size={19} />}
					accent='#7C3AED'
					soft='#F5F3FF'
					selected={roleFilter === "admin"}
					onClick={() => toggleRoleFilter("admin")}
				/>
				<UserStatCard
					label='Suspended'
					value={statsAvailable ? stats.suspended : null}
					detail={statsAvailable ? `${stats.newUsers} new this month` : statsStatus}
					icon={<IconReport size={19} />}
					accent={stats.suspended > 0 ? "#D97706" : "#64748B"}
					soft={stats.suspended > 0 ? "#FFF7ED" : "#F1F5F9"}
				/>
			</Box>

			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					backgroundColor: "#FFFFFF",
					borderRadius: "14px",
					boxShadow: USERS_PANEL_SHADOW,
					border: `1px solid ${USERS_PANEL_BORDER}`,
					overflow: isMobile ? "visible" : "hidden",
					flexGrow: isMobile ? 0 : 1,
					marginTop: "14px",
					minHeight: isMobile ? "auto" : 0
				}}
			>
				<Box sx={{ padding: "4px 16px 0" }}>
					<UnderlineTabs tabs={USER_TABS} active={activeTab} onChange={setActiveTab} label='User lists' />
				</Box>
				<Box sx={{ height: isMobile ? "auto" : "100%", minHeight: 0, display: "flex", flexDirection: "column", flexGrow: 1 }}>
					{activeTab === "All users" ? <UsersList initialRole={roleFilter} onRoleChange={setRoleFilter} /> : <InvitesList onReinvite={handleAddUser} />}
				</Box>
			</Box>
		</Page>
	)
}
